"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, Phone, XIcon } from "@/lib/icons";

const navItems = [
  { href: "/#home", label: "Home" },
  { href: "/#services", label: "Services" },
  { href: "/#industries", label: "Industries" },
  { href: "/#why", label: "Why Choose Us" },
  { href: "/#about", label: "About" },
  { href: "/#contact", label: "Contact" },
];

export function FloatingNav() {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [headerMorphState, setHeaderMorphState] = React.useState<'idle' | 'opening' | 'closing'>('idle');
  const [headerWidth, setHeaderWidth] = React.useState<number | null>(null);
  const [dropdownTop, setDropdownTop] = React.useState<number | null>(null);
  const headerRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Update header dimensions for dropdown alignment
  React.useEffect(() => {
    const updateHeaderDimensions = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setHeaderWidth(rect.width);
        // Position dropdown exactly at the bottom edge, accounting for border
        setDropdownTop(rect.bottom - 1); // Subtract 1px to overlap slightly for seamless connection
      }
    };

    updateHeaderDimensions();
    window.addEventListener("resize", updateHeaderDimensions);
    window.addEventListener("scroll", updateHeaderDimensions);
    return () => {
      window.removeEventListener("resize", updateHeaderDimensions);
      window.removeEventListener("scroll", updateHeaderDimensions);
    };
  }, [isMobileMenuOpen, isScrolled]);

  // Cleanup scroll lock on component unmount
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle mobile menu toggle with smooth animation and scroll lock
  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      setIsAnimating(true);
      setHeaderMorphState('closing');
      // Re-enable background scrolling
      document.body.style.overflow = 'unset';
      setTimeout(() => {
        setIsMobileMenuOpen(false);
        setIsAnimating(false);
        setHeaderMorphState('idle');
      }, 250);
    } else {
      setHeaderMorphState('opening');
      setIsMobileMenuOpen(true);
      // Prevent background scrolling when menu is open
      document.body.style.overflow = 'hidden';
      // Reset header morphing state after transition
      setTimeout(() => {
        setHeaderMorphState('idle');
      }, 350);
    }
  };

  // Close menu when clicking on a link
  const handleLinkClick = () => {
    toggleMobileMenu();
  };

  return (
    <div className="relative">
      {/* Main Navigation Header */}
      <nav
        ref={headerRef}
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          "fixed top-6 inset-x-0 z-50",
          "bg-white/95 backdrop-blur-lg border border-gray-200/50",
          "px-3 sm:px-5 md:px-6 lg:px-8 xl:px-10 py-2 sm:py-3 md:py-3.5",
          "w-fit mx-auto max-w-[98vw] sm:max-w-[95vw]",
          // Base styling - override with animations
          headerMorphState === 'idle' && !isMobileMenuOpen ? "rounded-full shadow-md hover:shadow-lg" : "",
          headerMorphState === 'idle' && isMobileMenuOpen ? "rounded-t-3xl rounded-b-none shadow-2xl border-b-0" : "",
          // Smooth morphing transition for position
          "header-morph-transition",
          // Apply morphing animations
          headerMorphState === 'opening' ? "header-morph-opening" : "",
          headerMorphState === 'closing' ? "header-morph-closing" : "",
          isScrolled ? "top-4" : "top-6"
        )}
      >
        <div className="flex items-center gap-2 sm:gap-4 md:gap-5 lg:gap-6">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center flex-shrink-0 hover:opacity-80 transition-opacity duration-200"
            aria-label="DPCLR - Return to homepage"
          >
            <div className="relative h-5 w-20 md:h-6 md:w-32 lg:h-7 lg:w-28">
              <Image
                src="/DPCLR-logo.png"
                alt="DPCLR"
                fill
                priority
                className="object-contain object-left"
                style={{ imageRendering: 'crisp-edges' }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <ul 
            className="hidden lg:flex items-center space-x-4 xl:space-x-6"
            role="menubar"
            aria-label="Main menu"
          >
            {navItems.map((item) => (
              <li key={item.href} role="none">
                <Link
                  href={item.href}
                  className="text-gray-700 hover:text-blue-600 transition-all duration-200 font-medium text-[15px] relative group whitespace-nowrap"
                  role="menuitem"
                  aria-current={item.href === "#home" ? "page" : undefined}
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-200 group-hover:w-full" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>

          {/* CTA and Phone */}
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {/* Phone - Responsive with icon only on mobile */}
            <a
              href="tel:+441737647280"
              className="flex items-center gap-1 sm:gap-1.5 text-gray-700 hover:text-blue-600 transition-all duration-300 group min-w-0 flex-shrink"
              aria-label="Call us at 01737 647 280"
            >
              <div className="p-2 sm:p-1.5 rounded-full bg-gray-100 group-hover:bg-blue-50 transition-colors flex-shrink-0" aria-hidden="true">
                <Phone className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </div>
              <span className="sr-only sm:not-sr-only sm:text-sm font-medium truncate min-w-0 sm:max-w-none">
                01737 647 280
              </span>
            </a>

            {/* Get Quote - Always visible */}
            <Button
              asChild
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white inline-flex shadow-sm hover:shadow-md transition-all duration-200 text-xs md:text-sm font-medium px-3 md:px-5 py-1.5 md:py-2.5 h-8 md:h-10 flex-shrink-0 whitespace-nowrap"
            >
              <Link href="#contact">
                <span>Get a Quote</span>
              </Link>
            </Button>

            {/* Elastic Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleMobileMenu}
              className={cn(
                "lg:hidden ml-0.5 rounded-full h-8 w-8 sm:h-9 sm:w-9 transition-all duration-300 flex-shrink-0",
                isMobileMenuOpen 
                  ? "bg-blue-600 text-white hover:bg-blue-700" 
                  : "hover:bg-gray-100"
              )}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              <div className="relative">
                <Menu 
                  className={cn(
                    "h-3.5 w-3.5 sm:h-4 sm:w-4 transition-all duration-300",
                    isMobileMenuOpen ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
                  )} 
                  aria-hidden="true" 
                />
                <XIcon 
                  className={cn(
                    "h-3.5 w-3.5 sm:h-4 sm:w-4 absolute inset-0 transition-all duration-300",
                    isMobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-75"
                  )} 
                  aria-hidden="true" 
                />
              </div>
            </Button>
          </div>
        </div>
      </nav>

      {/* Organic Growing Dropdown Menu */}
      <div
        className={cn(
          "fixed z-40 left-1/2 -translate-x-1/2 lg:hidden",
          "bg-white/95 backdrop-blur-lg border-x border-b border-gray-200/50",
          "rounded-b-3xl shadow-2xl",
          "max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain mobile-menu-scroll",
          // Ensure seamless connection
          "border-t-0 -mt-px",
          // Animation states - organic growing effect
          isMobileMenuOpen && !isAnimating
            ? "mobile-menu-organic-grow" 
            : isAnimating 
              ? "mobile-menu-organic-shrink opacity-100 pointer-events-none"
              : "opacity-0 pointer-events-none scale-y-0 origin-top"
        )}
        style={{
          // Match the exact width and position of the header
          width: headerWidth ? `${headerWidth}px` : 'auto',
          top: dropdownTop ? `${dropdownTop}px` : (isScrolled ? '4.25rem' : '6.25rem'),
          // Add transform origin at the connection point
          transformOrigin: 'top center'
        }}
        aria-hidden={!isMobileMenuOpen}
      >
        <nav 
          className="px-6 py-6 space-y-1"
          role="navigation"
          aria-label="Mobile menu"
        >
          {navItems.map((item, index) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={handleLinkClick}
              className={cn(
                "block px-4 py-3 text-lg font-medium rounded-xl",
                "text-gray-700 hover:text-blue-600 hover:bg-blue-50/50",
                "transition-all duration-200 transform hover:scale-[1.02]",
                "border border-transparent hover:border-blue-100",
                isMobileMenuOpen ? "mobile-menu-item" : ""
              )}
              style={{ 
                animationDelay: isMobileMenuOpen ? `${(index + 1) * 40}ms` : '0ms' 
              }}
              role="menuitem"
              aria-current={item.href === "#home" ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
          
          {/* Contact Section */}
          <div className="pt-4 mt-4 border-t border-gray-200/50 space-y-3">
            <a
              href="tel:+441737647280"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50/50 rounded-xl transition-all duration-200"
              aria-label="Call us at 01737 647 280"
            >
              <div className="p-2 rounded-full bg-gray-100" aria-hidden="true">
                <Phone className="h-4 w-4" />
              </div>
              <div>
                <div className="font-bold text-base">Call us</div>
                <div className="text-sm font-medium text-gray-500">01737 647 280</div>
              </div>
            </a>
            
            <Button 
              asChild 
              className="w-full rounded-xl h-10 md:h-12 bg-blue-600 hover:bg-blue-700 text-sm text-base text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02]"
            >
              <Link href="#contact" onClick={handleLinkClick} role="button">
                Get Free Quote
              </Link>
            </Button>
          </div>
        </nav>
      </div>

      {/* Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
